/** Translate a Menus row into an SPA route.
 *
 * The menu tree comes from the database, where `front_link` is empty on 95 of
 * the 96 live rows and `backend_link` holds the legacy Django path. Those paths
 * follow a small, regular set of patterns, so the route is derived from them
 * rather than kept in a per-slug table that would go stale as menus are added.
 *
 * Returns null when the row is a group header (no link, or '#') or points at a
 * legacy screen the SPA does not implement. Callers must render those as plain
 * labels: linking them to a non-existent route is what produced the
 * /menu/<slug> 404s.
 */

/** Legacy paths that map to a fixed SPA route. Keys are matched after
 * stripping any query string and normalising to a single trailing slash. */
const EXACT: Record<string, string> = {
  '/users/': '/admin/users',
  '/api/dept-user-listing/': '/admin/users',
  '/sanction/': '/sanction',
  '/sanction/pdna-budget/': '/sanction/budgets',
  '/funds/': '/funds',
  '/funds/budget/': '/funds/budgets',
  '/funds/portal/schemes/': '/funds/portal',
  '/master/location-import/': '/imports/locations',
  '/rep/eco-stats-rep/': '/reports/eco-stats',
  '/d1/summary/': '/dashboard/summary',
  '/d1/dash/': '/dashboard',
  '/web/disaster/': '/whatsapp/disasters',
  '/web/panchayathsecretaries/': '/whatsapp/panchayath-secretaries',
  '/web/patwaris/': '/whatsapp/patwaris',
  '/web/volunteers/': '/whatsapp/volunteers',
  '/weather-dashboard/': '/weather',
  // Menus id 17. Granted to 36,707 of 36,722 active accounts and the
  // only leaf menu of 92 with no route, so it was silently dropped
  // from every sidebar.
  '/web/api/sdmatags/': '/sdma-tags',
};

const AGGREGATE = /^\/web\/aggregate\/(\d+)\/?$/;
const DASHBOARD = /^\/d1\/dash\/([\w-]+)\/?$/;
/** Damage lists: /web/<any-department-slug>/<survey id>/list */
const DATA_ENTRY = /^\/web\/[^/]+\/(\d+)\/list\/?$/;

function normalise(link: string): string {
  const path = link.split('?')[0].split('#')[0].trim();
  if (!path || path === '/') return '';
  return path.endsWith('/') ? path : `${path}/`;
}

export function menuRouteFor(menu: {
  front_link?: string | null;
  backend_link?: string | null;
}): string | null {
  // An explicit front_link is authoritative: it is already an SPA path.
  const front = (menu.front_link ?? '').trim();
  if (front && front !== '#') return front;

  const link = normalise(menu.backend_link ?? '');
  if (!link) return null;

  if (EXACT[link]) return EXACT[link];

  const aggregate = AGGREGATE.exec(link);
  if (aggregate) return `/aggregates/${aggregate[1]}`;

  const dashboard = DASHBOARD.exec(link);
  if (dashboard) return `/dashboard/${dashboard[1]}`;

  const dataEntry = DATA_ENTRY.exec(link);
  if (dataEntry) return `/data-entry/${dataEntry[1]}`;

  return null;
}
